/**
 * Shared visual definitions for the Stepper's three step states (PRO-526).
 *
 * The stepper presents exactly three states so the colour language is
 * unambiguous:
 *   - completed : solid green fill with a tick
 *   - viewing   : blue outline on white (the step currently on screen)
 *   - pending   : grey outline on white (not yet reached)
 *
 * Both `StepperHeader` and `StepperLegend` consume these maps so the swatch
 * in the legend always matches the rendered step circle.
 */
export type StepState = "completed" | "viewing" | "pending";

export const STEP_STATES: readonly StepState[] = [
	"completed",
	"viewing",
	"pending",
] as const;

/** Circle (badge) classes per state. All states carry a 2px ring so the
 * circle diameter stays consistent between solid and outline variants. */
export const STEP_CIRCLE_CLASSES: Record<StepState, string> = {
	completed: "bg-utility-success-700 border-2 border-utility-success-700 text-white",
	viewing: "bg-white border-2 border-utility-brand-600 text-utility-brand-700",
	pending: "bg-white border-2 border-primary-300 text-primary-400",
};

/** Label/text classes per state. */
export const STEP_TEXT_CLASSES: Record<StepState, string> = {
	completed: "text-utility-success-700",
	viewing: "text-utility-brand-700 font-semibold",
	pending: "text-primary-400",
};

/** Human-readable label for each state, used by the legend. */
export const STEP_STATE_LABELS: Record<StepState, string> = {
	completed: "Complete",
	viewing: "Currently viewing",
	pending: "Pending",
};
